import { useState } from 'react';
import { api, ApiRequestError, openAuthenticatedFile } from '../api/client.js';
import { toApiPosition, type CartLine, type Quote } from '../api/types.js';
import { QuoteView } from './QuoteView.js';

/**
 * Angebot erstellen, M6.
 *
 * Die Preise stammen ausnahmslos vom Server. Diese Ansicht zeigt sie an und rechnet
 * nichts nach -- eine Zahl im Frontend waere eine zweite Preiswahrheit.
 *
 * Genau eine sichtbare Aktion: "Angebot erstellen". Es gibt bewusst keinen
 * eigenstaendigen, sichtbaren Entwurfsschritt mehr davor, der nahezu dieselben
 * Positionen und Preise noch einmal zeigt wie die Angebotsvorschau
 * (`SelectionList`) -- der Entwurf entsteht weiterhin serverseitig (er bleibt
 * die technische Grundlage fuer Ausstellen und PDF), aber nicht mehr als eigener
 * Oberflaechenschritt.
 *
 * Jeder Klick nimmt die zu diesem Zeitpunkt aktuellen Warenkorbpositionen --
 * nie einen zuvor eingefrorenen Zustand. Das ist nicht nur UX-Vereinfachung: Der
 * vorherige zweistufige Ablauf (Entwurf anlegen, spaeter ausstellen) konnte einen
 * Entwurf ausstellen, der nicht mehr zum inzwischen veraenderten Warenkorb passte
 * -- z. B. wenn nach einer uebernommenen G-ST-P/OE-Konfiguration noch
 * Standardartikel hinzukamen. Ohne einen ueber mehrere Klicks gehaltenen
 * Zwischenzustand kann diese Positionsart nicht mehr verloren gehen.
 *
 * Nach dem Ausstellen ist die wichtigste Aussage die Angebotsnummer und der Weg zum
 * PDF; beides steht deshalb ganz oben und ohne Suchen.
 */
export function QuotePanel({
  positions,
  freight,
  onQuoteChange,
  onIssued,
  onReset,
  recipient,
}: {
  positions: readonly CartLine[];
  /**
   * Verpackung und Fracht Inland: ausschliesslich fuer interne Rollen
   * (Schnellangebot). Weggelassen bedeutet: kein Ueberschreiben mitschicken --
   * der Kundenworkflow (`CustomerApp`) laesst dieses Feld deshalb weg, siehe
   * `SelectionList`.
   */
  freight?: string;
  /** Meldet der App, ob gerade ein Angebot offen ist -- sie haelt das Feld dann sichtbar. */
  onQuoteChange: (offen: boolean) => void;
  onIssued: () => void;
  onReset: () => void;
  /**
   * Schnellangebot fuer interne Rollen: frei genannter Empfaenger und frei
   * vergebener Rabattsatz, ohne Konto. Weggelassen bedeutet unveraendertes
   * Verhalten -- der Kundenworkflow kennt dieses Feld nicht.
   */
  recipient?: { name: string; number?: string; discountPercent: number };
}): React.JSX.Element {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Erzeugt aus den aktuellen Warenkorbpositionen einen Entwurf und stellt ihn
   * unmittelbar danach aus -- zwei bestehende Aufrufe nacheinander, keine neue
   * Serverlogik. Die Angebotsnummer entsteht wie bisher erst im zweiten Aufruf
   * (`QuoteService.issue`), nie hier und nie bei einer blossen Vorschau.
   *
   * Schlaegt der zweite Aufruf fehl (z. B. fehlende Pauschale, keine aktive
   * Vorlage), bleibt der zuvor angelegte Entwurf unveraendert als Entwurf
   * bestehen -- exakt das bisherige Fehlerverhalten. Ein erneuter Klick beginnt
   * bewusst wieder beim aktuellen Warenkorb statt bei diesem Entwurf, damit
   * zwischenzeitliche Aenderungen nie uebergangen werden.
   */
  async function angebotErstellen(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const entwurf = await api.post<Quote>('/api/quotes', {
        positions: positions.map(toApiPosition),
        ...(freight !== undefined && freight.trim() !== ''
          ? { packagingFreightOverride: freight.trim() }
          : {}),
        ...(recipient !== undefined
          ? {
              recipientName: recipient.name,
              discountPercent: recipient.discountPercent,
              ...(recipient.number !== undefined && recipient.number !== ''
                ? { recipientNumber: recipient.number }
                : {}),
            }
          : {}),
      });
      const ausgestellt = await api.post<Quote>(`/api/quotes/${entwurf.quoteId}/issue`);
      setQuote(ausgestellt);
      onQuoteChange(true);
      onIssued();
    } catch (e) {
      setError(fehlertext(e));
    } finally {
      setBusy(false);
    }
  }

  if (quote === null) {
    return (
      <section className="card">
        <h3>Angebot</h3>
        <p className="note">
          Erzeugt aus den uebernommenen Positionen ein verbindliches Angebot mit
          Angebotsnummer und PDF. Alle Preise werden dabei serverseitig berechnet.
        </p>
        {error !== null && <p role="alert">{error}</p>}
        <button
          type="button"
          className="primary"
          disabled={positions.length === 0 || busy}
          onClick={() => void angebotErstellen()}
        >
          Angebot erstellen
        </button>
      </section>
    );
  }

  return (
    <section className="card">
      <h3>Angebot ausgestellt</h3>

      <div className="erfolg" role="status">
        <strong>Angebot {quote.quoteNumber} wurde erstellt.</strong>
        <div className="row">
          <button
            type="button"
            className="primary"
            onClick={() => {
              void openAuthenticatedFile(`/api/quotes/${quote.quoteId}/pdf`).catch((e) =>
                setError(fehlertext(e)),
              );
            }}
          >
            PDF anzeigen
          </button>
        </div>
        {quote.validUntil !== null && (
          <p className="note">Gueltig bis {quote.validUntil}.</p>
        )}
        <button
          type="button"
          onClick={() => {
            setQuote(null);
            onReset();
          }}
        >
          Neues Angebot beginnen
        </button>
      </div>

      <QuoteView quote={quote} />

      {error !== null && <p role="alert">{error}</p>}
    </section>
  );
}

/** Fachliche Meldungen des Servers unveraendert zeigen -- sie sind verstaendlich. */
function fehlertext(e: unknown): string {
  return e instanceof ApiRequestError ? e.message : 'Die API ist nicht erreichbar.';
}
